import { ref, computed, watch } from "@vue/composition-api";
// import { title } from '@core/utils/filter'
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import moment from "moment-timezone";
import store from "@/store";
import ability from "@/libs/acl/ability";
import { debounce } from "lodash";
import useFilterCounter from "@/components/Filters/useFiltersCount";

export default function useList() {
  const refListTable = ref(null);

  const toast = useToast();

  store.dispatch("unit/getAll");
  store.dispatch("billing_provider/getAll");

  const tableColumns = [
    { label: "actions.name", key: "actions" },
    { label: "billing_finance.month", key: "month", sortable: true },
    { label: "billing_finance.year", key: "year", sortable: true },
    { label: "billing_finance.region", key: "region", sortable: true },
    { label: "billing_finance.state", key: "state", sortable: true },
    { label: "billing_finance.rems", key: "unit.rems", sortable: true },
    {
      label: "billing_finance.security_company",
      key: "provider.name",
      sortable: true,
    },
    { label: "billing_finance.job_description", key: "job_description", sortable: true },
    {
      label: "billing_finance.number_of_services",
      key: "number_of_services",
      sortable: true,
    },
    { label: "billing_finance.service_days", key: "service_days", sortable: true },
    { label: "billing_finance.hours", key: "hours", sortable: true },
    { label: "billing_finance.cost_per_shift", key: "cost_per_shift", sortable: true },
    { label: "billing_finance.type_of_guard", key: "type_of_guard", sortable: true },
    { label: "billing_finance.work_schedule", key: "work_schedule", sortable: true },
    {
      label: "date.created_at",
      key: "created_at",
      sortable: true,
      formatter: "formatDateAssigned",
    },
    {
      label: "date.updated_at",
      key: "updated_at",
      sortable: true,
      formatter: "formatDateAssigned",
    },
  ];

  const action = ref("list");

  if (
    ability.can("update", "BillingFinance") == true ||
    ability.can("delete", "BillingFinance") == true
  ) {
    tableColumns.splice(0, 0, { label: "actions.name", key: "actions" });
  }

  const perPage = ref(10);
  const totalList = ref(0);
  const currentPage = ref(1);
  const perPageOptions = [10, 25, 50, 100];
  const searchQuery = ref("");
  const sortBy = ref("id");
  const isSortDirDesc = ref(true);

  const yearFilter = ref(null);
  const remsFilter = ref(null);
  const unitFilter = ref([]);
  const monthFilter = ref(null);
  const regionFilter = ref(null);
  const companyFilter = ref(null);
  const typeOfGuardFilter = ref(null);

  const isDeleting = ref(false);

  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    };
  });

  const getFilters = () => {
    let filters = {};

    if (monthFilter.value) {
      filters.month = monthFilter.value;
    }

    if (yearFilter.value) {
      filters.year = yearFilter.value;
    }

    if (remsFilter.value) {
      filters.rems = remsFilter.value;
    }

    if (regionFilter.value) {
      filters.region = regionFilter.value;
    }

    if (companyFilter.value) {
      filters.company = companyFilter.value;
    }

    if (typeOfGuardFilter.value) {
      filters.type_of_guard = typeOfGuardFilter.value;
    }

    if (unitFilter.value?.length) {
      filters.unit = unitFilter.value;
    }

    return filters;
  };

  const fetchList = (ctx, callback) => {
    const filters = getFilters();

    const params = {
      q: searchQuery.value,
      max: perPage.value,
      "page[n]": currentPage.value,
      sortBy: sortBy.value,
      sortDesc: isSortDirDesc.value,
      action: action.value,
      ...filters,
    };

    store
      .dispatch("billing_finance/getAll", params)
      .then((response) => {
        if (action.value == "export") {
          action.value = "list";
        } else {
          callback(response.data);
          totalList.value = response.meta.total;
        }
      })
      .catch((error) => {
        toast({
          component: ToastificationContent,
          props: {
            title: "Error obteniendo la información",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      });
  };

  const fetchDelete = (onSuccess) => {
    isDeleting.value = true;

    const filters = getFilters();

    const params = {
      q: searchQuery.value,
      max: perPage.value,
      "page[n]": currentPage.value,
      sortBy: sortBy.value,
      sortDesc: isSortDirDesc.value,
      action: action.value,
      ...filters,
    };

    store
      .dispatch("billing_finance/deleteAll", params)
      .then((response) => {
        if (onSuccess) onSuccess();
        refetchData();

        toast({
          component: ToastificationContent,
          props: {
            title: response.message,
            text: response.data.total_rows + " registros eliminados",
            icon: "CheckIcon",
            variant: "success",
          },
        });
      })
      .catch((error) => {
        toast({
          component: ToastificationContent,
          props: {
            title: "Error eliminando la información",
            icon: "AlertTriangleIcon",
            variant: "danger",
          },
        });
      })
      .finally(() => {
        isDeleting.value = false;
      });
  };

  const formatDateAssigned = (value) => {
    const formattedDate = moment(value).format("DD/MM/YYYY HH:mm:ss");
    return formattedDate;
  };

  const formatCurrency = (amount) => {
    const formattedCurrency = new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
    }).format(amount);

    return formattedCurrency;
  };

  const refetchData = () => {
    refListTable.value.refresh();
  };

  watch([currentPage, perPage], () => {
    refetchData();
  });

  watch(
    [searchQuery],
    debounce(() => {
      refetchData();
    }, 500)
  );

  // filters counter ============================
  const filters = ref({});
  const filtersCount = useFilterCounter(filters);

  const buildFilters = () => {
    filters.value = {
      month: monthFilter.value,
      year: yearFilter.value,
      rems: remsFilter.value,
      region: regionFilter.value,
      company: companyFilter.value,
      type_of_guard: typeOfGuardFilter.value,
      unit: unitFilter.value,
    }
    refetchData();
  };

  const clearSearchFilters = () => {
    yearFilter.value = null;
    remsFilter.value = null;
    unitFilter.value = [];
    monthFilter.value = null;
    regionFilter.value = null;
    companyFilter.value = null;
    typeOfGuardFilter.value = null;

    buildFilters();
  };

  const unitOptions = computed(() => store.getters["unit/getSelectedItems"]);
  const providerOptions = computed(
    () => store.getters["billing_provider/getSelectedItems"]
  );

  return {
    action,
    tableColumns,
    perPage,
    currentPage,
    totalList,
    fetchList,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refListTable,
    formatDateAssigned,
    refetchData,
    formatCurrency,
    filtersCount,
    buildFilters,
    clearSearchFilters,
    unitOptions,
    yearFilter,
    remsFilter,
    unitFilter,
    regionFilter,
    companyFilter,
    typeOfGuardFilter,
    monthFilter,
    providerOptions,
    fetchDelete,
    isDeleting,
  };
}

